import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { labelTextColor } from '../../utils/labelColor';
import {
  CLOSE_WINDOWS,
  CLOSE_WINDOW_LABELS,
  EMPTY_LEAD_BOARD_FILTERS,
  hasActiveLeadFilters,
  type CloseWindow,
  type LeadBoardFilters,
} from '../../utils/leadBoardFilters';
import type { LeadLabel, LeadSource, LeadStage } from '../../services/api/leads';

export interface LeadFilterOwner {
  id: number;
  firstName: string | null;
  lastName: string | null;
}

interface LeadFilterBarProps {
  filters: LeadBoardFilters;
  onChange: (next: LeadBoardFilters) => void;
  owners: LeadFilterOwner[];
  sources: LeadSource[];
  stages: LeadStage[];
  labels: LeadLabel[];
  /** Hidden for a signed-out or unknown user — see `mineOnly`'s fail-closed rule. */
  canFilterMine: boolean;
  /** How many leads survive the current filters, for the summary line. */
  visibleCount: number;
  totalCount: number;
}

function ownerName(owner: LeadFilterOwner): string {
  if (owner.lastName && owner.firstName) return `${owner.lastName} ${owner.firstName}`;
  return owner.firstName || owner.lastName || `#${owner.id}`;
}

/** Add or remove `value` from `list` — the one interaction every chip has. */
function toggle<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

const selectClass =
  'rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-700 ' +
  'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500';

/**
 * MKS-347 — search and filters above the Leads board.
 *
 * Deliberately a controlled component with no state of its own: the page owns
 * `filters` and round-trips it through the URL, so a filtered board is a
 * shareable link and the bar cannot drift from what the board is showing.
 *
 * The multi-value slots (owner, source, stage) are native multi-selects
 * rather than bespoke popovers — they are keyboard-accessible for free and
 * this board already carries enough custom drag machinery.
 */
export default function LeadFilterBar({
  filters,
  onChange,
  owners,
  sources,
  stages,
  labels,
  canFilterMine,
  visibleCount,
  totalCount,
}: LeadFilterBarProps) {
  const active = hasActiveLeadFilters(filters);

  const multiValue = (ids: (number | null)[]) =>
    ids.map((id) => (id === null ? 'none' : String(id)));

  const readMulti = (e: React.ChangeEvent<HTMLSelectElement>): string[] =>
    Array.from(e.target.selectedOptions, (o) => o.value);

  return (
    <div className="mb-4 space-y-2" data-testid="lead-filter-bar">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={filters.search}
            onChange={(e) => onChange({ ...filters, search: e.target.value })}
            placeholder="Search lead, company, contact"
            aria-label="Search leads"
            className={`${selectClass} w-64 pl-8`}
          />
        </div>

        {canFilterMine && (
          <button
            type="button"
            aria-pressed={filters.mineOnly}
            onClick={() => onChange({ ...filters, mineOnly: !filters.mineOnly })}
            className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
              filters.mineOnly
                ? 'border-primary-600 bg-primary-600 text-white'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            My deals
          </button>
        )}

        <select
          multiple
          size={1}
          aria-label="Filter by owner"
          value={multiValue(filters.ownerIds)}
          onChange={(e) =>
            onChange({ ...filters, ownerIds: readMulti(e).map(Number).filter(Number.isInteger) })
          }
          className={`${selectClass} h-9 min-w-[9rem]`}
        >
          {owners.map((owner) => (
            <option key={owner.id} value={owner.id}>
              {ownerName(owner)}
            </option>
          ))}
        </select>

        <select
          multiple
          size={1}
          aria-label="Filter by source"
          value={multiValue(filters.sourceIds)}
          onChange={(e) =>
            onChange({
              ...filters,
              sourceIds: readMulti(e).map((v) => (v === 'none' ? null : Number(v))),
            })
          }
          className={`${selectClass} h-9 min-w-[9rem]`}
        >
          <option value="none">No source</option>
          {sources.map((source) => (
            <option key={source.id} value={source.id}>
              {source.name}
            </option>
          ))}
        </select>

        <select
          multiple
          size={1}
          aria-label="Filter by stage"
          value={multiValue(filters.stageIds)}
          onChange={(e) =>
            onChange({ ...filters, stageIds: readMulti(e).map(Number).filter(Number.isInteger) })
          }
          className={`${selectClass} h-9 min-w-[9rem]`}
        >
          {stages.map((stage) => (
            <option key={stage.id} value={stage.id}>
              {stage.name}
            </option>
          ))}
        </select>

        <select
          aria-label="Filter by expected close"
          value={filters.closeWindow ?? ''}
          onChange={(e) =>
            onChange({
              ...filters,
              closeWindow: (e.target.value || null) as CloseWindow | null,
            })
          }
          className={`${selectClass} h-9`}
        >
          <option value="">Expected close: any</option>
          {CLOSE_WINDOWS.map((w) => (
            <option key={w} value={w}>
              {CLOSE_WINDOW_LABELS[w]}
            </option>
          ))}
        </select>

        {active && (
          <button
            type="button"
            onClick={() => onChange(EMPTY_LEAD_BOARD_FILTERS)}
            className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
          >
            <XMarkIcon className="h-4 w-4" />
            Clear filters
          </button>
        )}

        {active && (
          <span className="text-xs text-gray-500" data-testid="lead-filter-count">
            {visibleCount} of {totalCount} leads
          </span>
        )}
      </div>

      {labels.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500">Filter by label:</span>
          {labels.map((label) => {
            const on = filters.labelIds.includes(label.id);
            return (
              <button
                key={label.id}
                type="button"
                aria-pressed={on}
                onClick={() => onChange({ ...filters, labelIds: toggle(filters.labelIds, label.id) })}
                style={
                  on
                    ? {
                        backgroundColor: label.color,
                        color: labelTextColor(label.color),
                        borderColor: label.color,
                      }
                    : { borderColor: label.color, color: label.color }
                }
                className="rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors"
              >
                {label.name}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
